/**
 * Calendrier mois en feuille basse — extrait de `ReserverScreen` pour être
 * partagé avec les écrans staff (consultation d'une date hors des 7 jours du
 * swiper). Le markup et les classes `.cal-*` sont repris à l'identique : le
 * rendu de l'écran Réserver est inchangé.
 *
 * Deux usages, réglés par les props :
 *   - client (Réserver) : `agenda` fourni + `allowPast` faux → les jours de
 *     fermeture sont barrés et le passé est désactivé (on ne réserve pas hier) ;
 *   - staff (consultation) : `agenda` à `null` + `allowPast` → toutes les dates
 *     sont ouvertes, y compris les services passés qu'on vient relire.
 */
import { useState } from 'react';
import type { Agenda } from '@alterrego/module-agenda';
import { getOpeningForDay, getSpecialForDate } from '@alterrego/module-agenda';
import './CalendarPopup.css';

const MONTHS_FULL = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const DAYS_HEADER = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function CalendarPopup({ today, agenda, onSelect, onClose, initialMonth, allowPast = false }: {
  today: Date;
  agenda: Agenda | null;
  onSelect: (d: Date) => void;
  onClose: () => void;
  /** Mois affiché à l'ouverture (par défaut celui de `today`). */
  initialMonth?: Date;
  /** Autorise la sélection d'une date passée (consultation staff). */
  allowPast?: boolean;
}) {
  const start = initialMonth ?? today;
  const [viewMonth, setViewMonth] = useState(start.getMonth());
  const [viewYear, setViewYear] = useState(start.getFullYear());

  const first = new Date(viewYear, viewMonth, 1);
  const lastDay = new Date(viewYear, viewMonth + 1, 0).getDate();
  let startDay = first.getDay() - 1;
  if (startDay < 0) startDay = 6;

  const cells: (number | null)[] = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const isOpen = (day: number) => {
    if (!agenda) return true;
    const d = new Date(viewYear, viewMonth, day);
    if (!getOpeningForDay(agenda, d.getDay())) return false;
    const special = getSpecialForDate(agenda, ymd(d));
    if (special?.type === 'closure') return false;
    return true;
  };

  const isPast = (day: number) => {
    if (allowPast) return false;
    const d = new Date(viewYear, viewMonth, day);
    d.setHours(0, 0, 0, 0);
    return d < today;
  };

  return (
    <div className="cal-overlay" onClick={onClose}>
      <div className="cal-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="cal-nav">
          <button onClick={prevMonth} aria-label="Mois précédent">‹</button>
          <span>{MONTHS_FULL[viewMonth]} {viewYear}</span>
          <button onClick={nextMonth} aria-label="Mois suivant">›</button>
        </div>
        <div className="cal-header-row">
          {DAYS_HEADER.map((d, i) => <span key={i}>{d}</span>)}
        </div>
        <div className="cal-grid">
          {cells.map((day, i) => {
            if (day === null) return <span key={i} className="cal-empty" />;
            const open = isOpen(day);
            const past = isPast(day);
            const disabled = !open || past;
            return (
              <button
                key={i}
                className={`cal-day ${disabled ? 'disabled' : ''} ${!open ? 'closed' : ''}`}
                disabled={disabled}
                onClick={() => onSelect(new Date(viewYear, viewMonth, day))}
              >
                {day}
              </button>
            );
          })}
        </div>
        <button className="cal-close" onClick={onClose}>Fermer</button>
      </div>
    </div>
  );
}
